import React, { useEffect, useState } from 'react'
import { Collapse, TextArea, InputGroup, Tag, Divider, Checkbox, Switch } from '@blueprintjs/core'
import { useLocationHealthChecks, ILocationHealthCheckHook } from './hooks/useLocationHealthChecks'
import { ILocation, ILocationHealthCheck } from '@api'
import { MultiCompanyLocationSelect, Button } from '@ui'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
import { fetchBookings } from '@utils/navigateToAphrodite'
import LocationSetting from './LocationSetting'

export default function LocationHealthChecks({
  disabledHealthCheckLocations,
  updateDisabled,
}: {
  disabledHealthCheckLocations: ILocation[]
  updateDisabled: (locs: ILocation[]) => Promise<void>
}) {
  const hook = useLocationHealthChecks()
  const [isUpdatingDisabled, setIsUpdatingDisabled] = useState(false)
  const [editDisabledLocations, setEditDisabledLocations] = useState(false)
  const [updatedDisabledLocs, setUpdatedDisabledLocs] = useState(disabledHealthCheckLocations)
  const [isDeletingDisabledLocs, setIsDeletingDisabledLocs] = useState(false)
  const reload = useReload()

  const disabledHealthCheckLocationIds = disabledHealthCheckLocations.map(({ id }) => id)

  // disabledIds for the disabled List
  const usedLocationIds = hook.healthChecks.reduce<string[]>((agg, curr) => {
    return [...agg, ...curr.locations.map(({ id }) => id)]
  }, [])

  return (
    <>
      <div className="mb-2">
        <div className="text-lg leading-normal font-medium mb-3">
          Disable Health Screenings by Location
        </div>
        <div className="text-sm text-grey-700 mb-3">
          Designate locations that will not receive health checks; people in disabled locations will
          not see buttons or messages relating to health checks
        </div>
        {editDisabledLocations ? (
          <MultiCompanyLocationSelect
            values={updatedDisabledLocs}
            onChange={(locs) => {
              setUpdatedDisabledLocs(locs)
            }}
            noTitle
            loading={isUpdatingDisabled}
            disabledIds={usedLocationIds}
            onClearAll={() => setUpdatedDisabledLocs([])}
            onSelectAll={(locations) => {
              if (locations.length === updatedDisabledLocs.length) {
                setUpdatedDisabledLocs([])
                return
              }
              setUpdatedDisabledLocs(locations)
            }}
          />
        ) : (
          disabledHealthCheckLocations.map((disabledLocation, i) => (
            <Tag key={i} round minimal style={{ padding: 10, fontSize: 14, marginRight: 10 }}>
              {disabledLocation.city}
            </Tag>
          ))
        )}
      </div>
      <div
        className={`flex ${
          Boolean(disabledHealthCheckLocations.length) ? 'justify-between' : 'justify-end'
        }`}>
        {Boolean(disabledHealthCheckLocations.length) && (
          <Button
            className="mr-3"
            minimal
            text="Delete Location Group"
            iconName="trash"
            intent="danger"
            loading={isDeletingDisabledLocs}
            onClick={() => {
              setIsDeletingDisabledLocs(true)
              updateDisabled([]).then(() => {
                setIsDeletingDisabledLocs(false)
                fetchBookings()
              })
            }}
          />
        )}

        {editDisabledLocations ? (
          <div>
            <Button
              className="mr-3"
              minimal
              text="Cancel"
              iconName="times"
              onClick={() => {
                setEditDisabledLocations(false)
                setUpdatedDisabledLocs(disabledHealthCheckLocations)
              }}
            />
            <Button
              minimal
              text="Save"
              iconName="check"
              intent="primary"
              loading={isUpdatingDisabled}
              onClick={() => {
                setIsUpdatingDisabled(true)
                updateDisabled(updatedDisabledLocs)
                  .then(() => {
                    setIsUpdatingDisabled(false)
                    setEditDisabledLocations(false)
                    fetchBookings()
                  })
                  .catch(() => setIsUpdatingDisabled(false))
              }}
            />
          </div>
        ) : (
          <Button
            className="mr-3"
            minimal
            blue
            text="Edit"
            iconName="pencil"
            onClick={() => {
              setEditDisabledLocations(true)
            }}
          />
        )}
      </div>
      <div className="my-4">
        <div>
          <div className="text-lg leading-normal font-medium mb-3">
            Health Check Message by Location
          </div>

          <div className="text-sm text-grey-700">
            Create a group of locations, then customize a health check message for the group.
          </div>
        </div>

        <LocationSetting
          locationList={hook.healthChecks}
          disabledLocations={disabledHealthCheckLocations}
          createButtonText="Add new Location Group"
          deleteButtonText="Delete Location Group"
          canExpand={true}
          onAddClose={() => {
            reload.locationHealthChecks()
          }}
          editable={true}
          deleteLocation={({ id }) => {
            return api.company.locationHealthChecks.remove({ id }).then(() => {
              reload.locationHealthChecks()
              fetchBookings()
            })
          }}
          deleteConfirmContent={() => (
            <div style={{ maxWidth: 400 }}>
              <div className="mb-2">Are you sure you want to delete this Location Group?</div>
              <div className="text-sm whitespace-pre-wrap text-grey-700">
                Deleting this group changes their health check message - they will receive the
                company-wide health check message.
              </div>
            </div>
          )}
          content={(data) => {
            return HealthCheck({
              ...data,
              disabledHealthCheckIds: disabledHealthCheckLocationIds,
              resetLocations: () => {
                reload.locationHealthChecks()
              },
              hook,
            })
          }}
          createContent={(locs: ILocation[]) => {
            return LocationHealthCheckCreate({
              locs,
              disabledHealthCheckIds: disabledHealthCheckLocationIds,
              hook,
            })
          }}
        />
      </div>
    </>
  )
}

function HealthCheck({
  id,
  healthScreeningMessage,
  healthScreeningConfirmButtonText,
  followUpQuestionEnabled,
  followUpQuestionRecipient,
  followUpQuestionText,
  healthScreeningOverride,
  locations,
  hook,
  isOpen,
  isEditing,
  resetLocations,
}: ILocationHealthCheck & {
  hook: ILocationHealthCheckHook
  isOpen: boolean
  isEditing: boolean
  resetLocations: () => void
}) {
  const [healthScreenMessage, setHealthScreenMessage] = useState<string>(
    healthScreeningMessage || ''
  )
  const [healthConfirmationMessage, setHealthConfirmationMessage] = useState<string>(
    healthScreeningConfirmButtonText || ''
  )
  const [isOverridingDefault, setIsOverridingDefault] = useState(healthScreeningOverride || false)
  const [isUsingFollowUp, setIsUsingFollowUp] = useState(followUpQuestionEnabled || false)
  const [followUpMessage, setFollowUpMessage] = useState(followUpQuestionText || '')
  const [followUpRecipient, setFollowUpRecipient] = useState(followUpQuestionRecipient || '')
  const reload = useReload()

  return {
    locationTitle: 'Location Group',
    cancelEdit: () => {
      setHealthScreenMessage(healthScreeningMessage || '')
      setHealthConfirmationMessage(healthScreeningConfirmButtonText || '')
      setIsOverridingDefault(healthScreeningOverride || false)
      setFollowUpMessage(followUpQuestionText || '')
      setIsUsingFollowUp(followUpQuestionEnabled || false)
      setFollowUpRecipient(followUpQuestionRecipient || '')
      resetLocations()
    },
    enableTooltip:
      !locations.length ||
      ((!healthScreenMessage || !healthConfirmationMessage) && isOverridingDefault) ||
      (isUsingFollowUp && (!followUpRecipient || !followUpMessage)),
    tooltipText: !isUsingFollowUp
      ? 'Please be sure you added Location(s), Local health check message and Confirmation button text.'
      : 'Please be sure you added Location(s), Local health check message, Confirmation button text, Follow Up Question and Follow Up Question Reciepient Email Address.',
    saveDisabled:
      !locations.length ||
      ((!healthScreenMessage || !healthConfirmationMessage) && isOverridingDefault) ||
      (isUsingFollowUp && (!followUpRecipient || !followUpMessage)),
    saveLocation: (locs: ILocation[]) => {
      return api.company.locationHealthChecks
        .update({
          locationIds: locs.map(({ id }) => id),
          healthScreeningConfirmButtonText: healthConfirmationMessage,
          healthScreeningMessage: healthScreenMessage,
          followUpQuestionEnabled: isUsingFollowUp,
          followUpQuestionRecipient: isUsingFollowUp ? followUpRecipient : undefined,
          followUpQuestionText: isUsingFollowUp ? followUpMessage : undefined,
          healthScreeningOverride: !!isOverridingDefault,
          id,
        })
        .then(() => {
          fetchBookings()
          reload.locationHealthChecks()
        })
    },
    element: (
      <div className="my-4" key={id}>
        <div className="flex my-4">
          <Checkbox
            checked={!isOverridingDefault}
            onChange={() => setIsOverridingDefault(!isOverridingDefault)}
            disabled={!isEditing}
          />
          Keep company-wide health check message for this location group
        </div>
        <div>
          <Collapse isOpen={isOpen}>
            {isOverridingDefault && (
              <div className="flex mt-2">
                <div className="mr-3 ">
                  <div>Local health check message </div>
                  <TextArea
                    style={{ width: 500, minHeight: 300 }}
                    onChange={({ target: { value } }) => setHealthScreenMessage(value)}
                    value={healthScreenMessage}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <div>Confirmation Button Text</div>
                  <InputGroup
                    style={{ width: 300 }}
                    onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                      setHealthConfirmationMessage(value)
                    }
                    value={healthConfirmationMessage}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            )}
            <Divider />

            <div className=" my-4">
              <div className="flex justify-between mb-4">
                <div>
                  <div className="text-lg leading-normal font-medium mb-3">
                    Post health check question
                  </div>

                  <div className="text-sm text-grey-700">
                    Create a post health question for your employees. After they complete their
                    health check, employees will see this message and choose a Yes or No response
                  </div>
                </div>
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={isUsingFollowUp}
                  onChange={() => setIsUsingFollowUp(!isUsingFollowUp)}
                  disabled={!isEditing}
                />
              </div>
              {isUsingFollowUp && (
                <div className="flex mt-2">
                  <div className="mr-3">
                    <div>Post health check question </div>
                    <TextArea
                      style={{ width: 500, minHeight: 300 }}
                      onChange={({ target: { value } }) => setFollowUpMessage(value)}
                      value={followUpMessage}
                      placeholder="Do you want us to order you lunch?"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <div>Email address of the responsible person (caterer or cleaning)</div>
                    <InputGroup
                      style={{ width: 300 }}
                      onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                        setFollowUpRecipient(value)
                      }
                      value={followUpRecipient}
                      placeholder="john.doe@company.com"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              )}
            </div>
          </Collapse>
        </div>
        <Divider />
      </div>
    ),
  }
}

function LocationHealthCheckCreate({
  locs,
  disabledHealthCheckIds,
  hook,
}: {
  locs: ILocation[]
  disabledHealthCheckIds: string[]
  hook: ILocationHealthCheckHook
}) {
  const [healthScreenMessage, setHealthScreenMessage] = useState('')
  const [healthConfirmationMessage, setHealthConfirmationMessage] = useState('')
  const [isOverridingDefault, setIsOverridingDefault] = useState(true)
  const [isUsingFollowUp, setIsUsingFollowUp] = useState(false)
  const [followUpMessage, setFollowUpMessage] = useState('')
  const [followUpRecipient, setFollowUpRecipient] = useState('')

  useEffect(() => {
    console.log()
  }, [followUpMessage, followUpRecipient, isUsingFollowUp])

  const excludedIds = hook.healthChecks.reduce<string[]>((agg, { locations }) => {
    const locIds = locations.map(({ id }) => id)
    return [...agg, ...locIds]
  }, [])

  return {
    newLocationText: 'New Location Group',
    enableTooltip:
      !locs.length ||
      ((!healthScreenMessage || !healthConfirmationMessage) && isOverridingDefault) ||
      (isUsingFollowUp && (!followUpRecipient || !followUpMessage)),
    tooltipText: !isUsingFollowUp
      ? 'Please be sure you added Location(s), Local health check message and Confirmation button text.'
      : 'Please be sure you added Location(s), Local health check message, Confirmation button text, Follow Up Question and Follow Up Question Reciepient Email Address.',
    saveDisabled:
      !locs.length || ((!healthScreenMessage || !healthConfirmationMessage) && isOverridingDefault),
    addLocation: (locs: ILocation[]) => {
      return api.company.locationHealthChecks
        .add({
          healthScreeningConfirmButtonText: healthConfirmationMessage,
          healthScreeningMessage: healthScreenMessage,
          locationIds: locs.map(({ id }) => id),
          followUpQuestionEnabled: isUsingFollowUp,
          followUpQuestionRecipient: isUsingFollowUp ? followUpRecipient : undefined,
          followUpQuestionText: isUsingFollowUp ? followUpMessage : undefined,
          healthScreeningOverride: !!isOverridingDefault,
        })
        .then(() => {
          fetchBookings()
        })
    },
    disabledLocationIds: [...disabledHealthCheckIds, ...excludedIds],
    element: (
      <div className="my-3">
        <div className="mb-3">New Location Group</div>

        <div className="flex my-4">
          <Checkbox
            checked={!isOverridingDefault}
            onChange={() => setIsOverridingDefault(!isOverridingDefault)}
          />
          Keep company-wide health check message for this location group
        </div>
        {isOverridingDefault && (
          <div className="flex mt-2 mb-4">
            <div className="mr-3">
              <div>Local health check message </div>
              <TextArea
                style={{ width: 500, minHeight: 300 }}
                onChange={({ target: { value } }) => setHealthScreenMessage(value)}
                value={healthScreenMessage}
                placeholder="Please add your custom Health Check Confirmation Message here..."
              />
            </div>
            <div>
              <div>Confirmation Button Text</div>
              <InputGroup
                style={{ width: 300 }}
                onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                  setHealthConfirmationMessage(value)
                }
                value={healthConfirmationMessage}
                placeholder="Confirm"
              />
            </div>
          </div>
        )}

        <Divider />

        <div className=" my-4">
          <div className="flex justify-between mb-4">
            <div>
              <div className="text-lg leading-normal font-medium mb-3">
                Post health check question
              </div>

              <div className="text-sm text-grey-700">
                Create a post health question for your employees. After they complete their health
                check, employees will see this message and choose a Yes or No response
              </div>
            </div>
            <Switch
              large
              innerLabelChecked="ON"
              innerLabel="OFF"
              checked={isUsingFollowUp}
              onChange={() => setIsUsingFollowUp(!isUsingFollowUp)}
            />
          </div>
          {isUsingFollowUp && (
            <div className="flex mt-2">
              <div className="mr-3">
                <div>Post health check question </div>
                <TextArea
                  style={{ width: 500, minHeight: 300 }}
                  onChange={({ target: { value } }) => setFollowUpMessage(value)}
                  value={followUpMessage}
                  placeholder="Do you want us to order you lunch?"
                />
              </div>
              <div>
                <div>Email address of the responsible person (caterer or cleaning)</div>
                <InputGroup
                  style={{ width: 300 }}
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                    setFollowUpRecipient(value)
                  }
                  value={followUpRecipient}
                  placeholder="john.doe@company.com"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    ),
  }
}
